import React, { useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { db } from '../firebase';
import { normalizeAvailability, resolveZone, zoneCity } from '../dateUtils';
import ProfileFields, { STATUSES } from './ProfileFields';
import { stepErrors } from './Onboarding';
import { profileDoc, uploadProfilePhoto, doubledSubjects } from '../profileSave';
import { examLabel } from '../names';
import { examMonthLabel } from '../examMonth';
import { useProgress, progressTotals } from '../progress';
import { Icon, Avatar } from './ui';
import { toast, confirmSheet } from './feedback';
import { openShareSheet, openInviteSheet } from './ShareSheet';

const DEFAULT_PROFILE = { name:'',age:'',gender:'',country:'United States',city:'',school:'',yearLevel:'',timezone:'',status:'medical_student',studyCategory:'',strengths:[],wantsToLearn:[],languages:['English'],availabilityLocal:[],availabilityTz:'',profilePicture:null };

// The saved profile as the form's state. Normalises legacy values: "America/New_York
// (EST/EDT)" -> bare IANA id, and the old "Other" country / city placeholder -> blank
// so the free fields start empty instead of showing a dead sentinel.
const fromSaved = (userProfile) => {
  const { local, tz } = normalizeAvailability(userProfile);
  const city = userProfile.city === 'Other - Please specify in profile' ? '' : (userProfile.city || '');
  const country = userProfile.country === 'Other' ? '' : (userProfile.country || DEFAULT_PROFILE.country);
  const languages = userProfile.languages?.length ? userProfile.languages : ['English'];
  return { ...DEFAULT_PROFILE, ...userProfile, country, city, languages, timezone: resolveZone(userProfile.timezone), availabilityLocal: local, availabilityTz: tz };
};

// The edit sheets, in ProfileFields' step order; Photo has its own controls.
const SECTIONS = [
  { key: 'about', step: 0, title: 'About you', icon: 'profile' },
  { key: 'exam', step: 1, title: 'Exam & subjects', icon: 'cap' },
  { key: 'languages', step: 2, title: 'Languages', icon: 'globe' },
  { key: 'week', step: 3, title: 'Availability', icon: 'calendar' },
  { key: 'photo', title: 'Photo', icon: 'upload' },
];

// Profile is a view (design F17): your card as partners see it, then one row per
// group, each opening its own edit sheet with Save in the header.
function Profile({ user, userProfile, onProfileUpdate, onOpen, unseenDocs = 0, showNotification }) {
  const profile = userProfile ? fromSaved(userProfile) : DEFAULT_PROFILE;
  const progress = useProgress(user.uid);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [picBusy, setPicBusy] = useState(false);

  const open = section => { setDraft({ ...profile, timezone: profile.timezone || resolveZone('') }); setErrors({}); setEditing(section); };
  const close = () => { if (!saving) { setEditing(null); setDraft(null); } };
  const set = (k, v) => { setDraft(d => ({ ...d, [k]: v })); setErrors(e => ({ ...e, [k]: undefined })); };
  const setCountry = c => { setDraft(d => ({ ...d, country: c, city: '' })); setErrors(e => ({ ...e, country: undefined })); };

  const save = async () => {
    const e = stepErrors(editing.step, draft);
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const data = profileDoc(draft, user.uid);
      await setDoc(doc(db, 'users', user.uid), data, { merge: true });
      onProfileUpdate({ ...userProfile, ...data });
      showNotification('Saved', 'Your profile has been updated.');
      setEditing(null); setDraft(null);
    } catch (err) {
      console.error(err);
      toast('Could not save your profile. Check your connection and try again.', { type: 'error' });
    } finally { setSaving(false); }
  };

  const pickPhoto = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast('Please choose an image file.', { type: 'error' }); return; }
    setPicBusy(true);
    try {
      const url = await uploadProfilePhoto(user.uid, file);
      onProfileUpdate({ ...userProfile, profilePicture: url });
      showNotification('Photo updated', 'Your profile picture has been saved.');
    } catch (err) {
      console.error(err);
      toast('Could not upload that photo: ' + (err.message || 'please try another image.'), { type: 'error' });
    } finally { setPicBusy(false); }
  };

  const removePhoto = async () => {
    if (!await confirmSheet({ title: 'Remove your photo?', message: 'Your profile will show your initial instead.', confirmLabel: 'Remove photo', danger: true })) return;
    setPicBusy(true);
    try {
      try { await deleteObject(ref(getStorage(), `profilePictures/${user.uid}/avatar.jpg`)); }
      catch (e) { console.warn('no stored photo to delete', e); }
      await setDoc(doc(db, 'users', user.uid), { profilePicture: '' }, { merge: true });
      onProfileUpdate({ ...userProfile, profilePicture: '' });
      showNotification('Photo removed', 'Your profile shows your initial again.');
    } catch (err) {
      console.error(err);
      toast('Could not remove the photo. Please try again.', { type: 'error' });
    } finally { setPicBusy(false); }
  };

  const status = (STATUSES.find(([v]) => v === profile.status) || [])[1];
  const hours = profile.availabilityLocal.length * 2;
  const summary = {
    about: [profile.age && `${profile.age}`, profile.gender, [profile.city, profile.country].filter(Boolean).join(', ')].filter(Boolean).join(' · ') || 'Add your details',
    exam: [examLabel(profile.studyCategory), examMonthLabel(profile.examMonth)].filter(Boolean).join(' · ') || 'Choose your exam',
    languages: profile.languages.join(', '),
    week: hours ? `${hours} h a week · ${zoneCity(profile.timezone)}` : 'Mark when you can study',
    photo: profile.profilePicture ? 'Change or remove' : 'Add a photo',
  };
  const totals = progress && progress.length ? progressTotals(progress) : null;

  const doubled = doubledSubjects(profile);

  return (
    <>
      {/* Older profiles can have a subject in both lists (review R6). */}
      {doubled.length > 0 && (
        <button type="button" className="card profile-notice" onClick={() => open(SECTIONS[1])}>
          <Icon name="alert" />
          <span className="row-link-text">{doubled.length === 1 ? `${doubled[0]} is` : `${doubled.length} subjects are`} in both lists. Choose where {doubled.length === 1 ? 'it belongs' : 'they belong'}.</span>
          <Icon name="chevron" />
        </button>
      )}
      <div className="card profile-card">
        <div className="profile-card-top">
          <Avatar src={profile.profilePicture} name={profile.name} size={72} />
          <div className="profile-card-who">
            <div className="match-name">{profile.name || 'Your name'}</div>
            <div className="match-meta">{[profile.yearLevel || status, profile.school, examMonthLabel(profile.examMonth)].filter(Boolean).join(' · ')}</div>
          </div>
        </div>
        {profile.studyCategory && <div className="profile-card-line"><span className="chip accent">{profile.studyCategory}</span></div>}
        {profile.strengths.length > 0 && <div className="profile-card-line"><span className="section-label">Can teach</span><div>{profile.strengths.map(s => <span key={s} className="chip">{s}</span>)}</div></div>}
        {profile.wantsToLearn.length > 0 && <div className="profile-card-line"><span className="section-label">Wants to learn</span><div>{profile.wantsToLearn.map(s => <span key={s} className="chip">{s}</span>)}</div></div>}
        <p className="small muted profile-card-note">This is how study partners see you.</p>
      </div>

      {/* Stats only once there's something to count (design F18). */}
      {totals ? (
        <div className="card">
          <div className="reputation-stats progress-stats">
            <div className="stat-card"><span className="stat-value">{totals.sessions}</span><span className="stat-label">{totals.sessions === 1 ? 'Session' : 'Sessions'}</span></div>
            <div className="stat-card"><span className="stat-value">{totals.hours} h</span><span className="stat-label">Studied</span></div>
          </div>
        </div>
      ) : progress && <p className="small muted profile-stats-note">Your stats appear after your first session.</p>}

      <div className="card profile-rows">
        {SECTIONS.map(sec => (
          <button key={sec.key} type="button" className="profile-row" onClick={() => open(sec)}>
            <Icon name={sec.icon} />
            <span className="profile-row-text"><span className="profile-row-title">{sec.title}</span><span className="profile-row-sub">{summary[sec.key]}</span></span>
            <Icon name="chevron" />
          </button>
        ))}
      </div>

      {/* One grouped card (review R7). */}
      <div className="card profile-rows">
        <button type="button" className="profile-row row-link" onClick={() => onOpen('materials')}>
          <Icon name="docs" />
          <span className="profile-row-text"><span className="profile-row-title">Shared docs</span></span>
          {unseenDocs > 0 && <span className="badge">{unseenDocs}</span>}
          <Icon name="chevron" />
        </button>
        <button type="button" className="profile-row row-link" onClick={() => onOpen('resources')}>
          <Icon name="book" />
          <span className="profile-row-text"><span className="profile-row-title">Resources</span></span>
          <Icon name="chevron" />
        </button>
        <button type="button" className="profile-row row-link" onClick={() => onOpen('progress')}>
          <Icon name="progress" />
          <span className="profile-row-text"><span className="profile-row-title">Progress</span></span>
          <Icon name="chevron" />
        </button>
        <button type="button" className="profile-row row-link" onClick={() => openInviteSheet(user.uid, userProfile)}>
          <Icon name="profile" />
          <span className="profile-row-text"><span className="profile-row-title">Invite a study partner</span><span className="profile-row-sub">Bring a classmate or friend. You'll be connected when they join.</span></span>
          <Icon name="chevron" />
        </button>
        <button type="button" className="profile-row row-link" onClick={() => openShareSheet()}>
          <Icon name="share" />
          <span className="profile-row-text"><span className="profile-row-title">Share MedXMeet</span></span>
          <Icon name="chevron" />
        </button>
      </div>

      {editing && (
        <div className="modal-overlay" onClick={close}>
          <div className="modal profile-sheet" role="dialog" aria-label={editing.title} onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <button type="button" className="btn-text" onClick={close} disabled={saving}>{editing.key === 'photo' ? 'Done' : 'Cancel'}</button>
              <h3>{editing.title}</h3>
              {editing.key === 'photo'
                ? <span className="profile-sheet-spacer" />
                : <button type="button" className="btn-small" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>}
            </div>
            {editing.key === 'photo' ? (
              <div className="onboarding-photo">
                <Avatar src={profile.profilePicture} name={profile.name} size={120} />
                <div className="profile-photo-actions">
                  <label className="btn-small secondary">
                    {picBusy ? 'Working…' : profile.profilePicture ? 'Change photo' : 'Add a photo'}
                    <input type="file" accept="image/*" onChange={pickPhoto} disabled={picBusy} hidden />
                  </label>
                  {profile.profilePicture && <button type="button" className="btn-small btn-decline" onClick={removePhoto} disabled={picBusy}>Remove</button>}
                </div>
              </div>
            ) : (
              <ProfileFields step={editing.step} p={draft} set={set} setCountry={setCountry} errors={errors} lead={false} />
            )}
          </div>
        </div>
      )}
    </>
  );
}
export default Profile;
